import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { motion } from "framer-motion";

const credibilityStats = [
  { label: "Years Experience", value: "20+" },
  { label: "Region", value: "Australia" },
  { label: "Qualification", value: "CPA" },
  { label: "Clients", value: "Startups & SMBs" }
];

const thinkingPoints = [
  "Keeping things simple",
  "Understanding what is actually driving results",
  "Turning numbers into decisions, not reports"
];

const services = [
  {
    number: "01",
    title: "Business Clarity",
    description: "Understanding what is really happening in your business, beyond surface-level numbers."
  },
  {
    number: "02",
    title: "Cash Flow & Financial Understanding",
    description: "Helping you see where money is coming from, where it is going, and what needs attention."
  },
  {
    number: "03",
    title: "Decision Support",
    description: "Breaking down options so you can make informed decisions with confidence."
  }
];

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="py-28 md:py-36 lg:py-44 relative overflow-hidden" data-testid="hero-section">
        {/* Background elements */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#F0F4F8] to-transparent -z-10" />
        <div className="absolute top-20 right-20 w-72 h-72 border-2 border-[#1565C0]/10 -z-10 hidden lg:block" />
        <div className="absolute bottom-20 right-40 w-32 h-32 bg-[#00897B]/10 -z-10 hidden lg:block" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="max-w-4xl"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="accent-bar"></div>
              <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#1565C0]">Business Mentoring</p>
            </div>
            
            <h1 className="font-['Cabinet_Grotesk',sans-serif] text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold tracking-tighter text-[#0D1B2A] leading-[1.02]" data-testid="hero-headline">
              Clarity,<br />
              <span className="gradient-text">not complexity</span>
            </h1>
            <p className="mt-10 text-xl md:text-2xl text-[#3D5A73] leading-relaxed max-w-2xl" data-testid="hero-subheading">
              I work with business owners to help them understand their numbers, make better decisions, and move forward with confidence.
            </p>
            <div className="mt-14 flex flex-col sm:flex-row gap-5">
              <Link
                to="/contact"
                className="btn-premium inline-flex items-center justify-center px-12 py-5 text-sm font-bold tracking-wide bg-[#1565C0] text-white hover:bg-[#0D47A1] transition-all shadow-lg shadow-[#1565C0]/25"
                data-testid="hero-cta-primary"
              >
                Get in touch
              </Link>
              <Link
                to="/about"
                className="btn-premium inline-flex items-center justify-center px-12 py-5 text-sm font-bold tracking-wide border-2 border-[#0D1B2A] text-[#0D1B2A] hover:bg-[#0D1B2A] hover:text-white transition-all"
                data-testid="hero-cta-secondary"
              >
                Learn more
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Quote */}
      <section className="py-20 highlight-box" data-testid="featured-quote-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="font-['Cormorant_Garamond',serif] text-2xl sm:text-3xl md:text-4xl lg:text-5xl italic text-white leading-relaxed">
              "Financial clarity is the most underrated business asset."
            </p>
            <div className="mt-8 flex items-center justify-center gap-4">
              <div className="w-12 h-0.5 bg-[#00897B]"></div>
              <p className="text-sm font-bold tracking-wide text-[#00897B]">
                Arora Sukhvinder
              </p>
              <div className="w-12 h-0.5 bg-[#00897B]"></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 border-b border-[#E0E7ED]" data-testid="intro-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="max-w-4xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="font-['Cormorant_Garamond',serif] text-2xl sm:text-3xl italic text-[#0D1B2A] leading-relaxed">
              "Most problems are not about effort. They are about <span className="text-[#1565C0] font-semibold">clarity</span>."
            </p>
            <p className="mt-8 text-lg text-[#3D5A73] leading-relaxed">
              Over the years, I have worked across large organisations and small businesses. When people understand what is really going on in their business, better decisions follow.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Credibility Strip */}
      <section className="py-16 bg-[#F0F4F8]" data-testid="credibility-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12"
            variants={stagger}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {credibilityStats.map((stat, index) => (
              <motion.div 
                key={index} 
                className="stat-fade text-center md:text-left"
                variants={fadeInUp}
                data-testid={`credibility-stat-${index}`}
              >
                <p className="font-['Cabinet_Grotesk',sans-serif] text-4xl md:text-5xl font-bold gradient-text tracking-tight">
                  {stat.value}
                </p>
                <p className="mt-2 text-sm font-medium text-[#607D8B] tracking-wide uppercase">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How I Think */}
      <section className="py-24 md:py-32" data-testid="how-i-think-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Left - Heading */}
            <motion.div 
              className="lg:col-span-4 lg:sticky lg:top-32 lg:self-start"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="accent-bar mb-6"></div>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00897B] mb-4">Approach</p>
              <h2 className="font-['Cabinet_Grotesk',sans-serif] text-4xl md:text-5xl font-bold tracking-tight text-[#0D1B2A]" data-testid="how-i-think-heading">
                How I think
              </h2>
            </motion.div>

            {/* Right - Content */}
            <div className="lg:col-span-8">
              <motion.p 
                className="text-xl text-[#3D5A73] mb-12 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                Most businesses are not short of ideas. They are short of clear thinking.
              </motion.p>
              
              {/* Focus Points */}
              <motion.div 
                className="highlight-box p-10 md:p-12 mb-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00897B] mb-8">I focus on</p>
                <div className="space-y-6">
                  {thinkingPoints.map((point, index) => (
                    <div 
                      key={index} 
                      className="flex items-center gap-5"
                      data-testid={`thinking-point-${index}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-[#00897B] flex items-center justify-center text-white font-bold text-sm">
                        {String(index + 1).padStart(2, '0')}
                      </div>
                      <span className="text-xl text-white font-medium">{point}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Quote card */}
              <motion.div 
                className="bg-[#F0F4F8] p-10 border-l-4 border-[#00897B]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <p className="font-['Cormorant_Garamond',serif] text-2xl md:text-3xl italic text-[#0D1B2A] leading-relaxed">
                  "When people understand what is really going on in their business, better decisions follow."
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 md:py-32 bg-[#F8FAFC] border-t border-[#E0E7ED]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="accent-bar mb-6"></div>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00897B] mb-4">Services</p>
            <h2 className="font-['Cabinet_Grotesk',sans-serif] text-4xl md:text-5xl font-bold tracking-tight text-[#0D1B2A]" data-testid="services-heading">
              Key areas of work
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="service-card bg-white p-10 border border-[#E0E7ED] group cursor-pointer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`service-card-${index}`}
              >
                <span className="text-sm font-bold text-[#1565C0]">{service.number}</span>
                <h3 className="font-['Cabinet_Grotesk',sans-serif] text-2xl font-bold text-[#0D1B2A] mt-4 mb-4 group-hover:text-[#1565C0] transition-colors">
                  {service.title}
                </h3>
                <p className="text-[#3D5A73] leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Link
              to="/what-i-do"
              className="group inline-flex items-center gap-3 text-[#1565C0] font-bold hover:text-[#0D47A1] transition-all"
              data-testid="services-see-all-link"
            >
              See all services <ArrowRight size={18} weight="bold" className="arrow-animate" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 md:py-40 bg-[#0D1B2A] relative overflow-hidden" data-testid="cta-section">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#1565C0]/10 rounded-full blur-3xl -z-0" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#00897B]/10 rounded-full blur-3xl -z-0" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="accent-bar mb-8"></div>
            <h2 className="font-['Cabinet_Grotesk',sans-serif] text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight" data-testid="cta-heading">
              Ready to get clarity?
            </h2>
            <p className="mt-8 text-xl text-[#90A4AE] leading-relaxed">
              If you want a clearer view of your business, feel free to reach out.
            </p>
            <Link
              to="/contact"
              className="btn-premium inline-flex items-center justify-center mt-12 px-10 py-5 text-sm font-bold tracking-wide bg-[#00897B] text-white hover:bg-[#00796B] transition-all shadow-lg shadow-[#00897B]/25"
              data-testid="cta-button"
            >
              Get in touch
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
